import { Tag, Timeline, Card } from "antd";
import { FileTextOutlined, DownloadOutlined } from "@ant-design/icons";
import trans from "../../../utils";
import { formatDateString } from "../../../JsHelpers/DateFormat";
import Button from "../../Helpers/Button/Button";

export default function InspectionRequestDeficiency({
  values,
  setActiveOption,
  resubmit,
  status,
  setResubmit,
  qaAction,
  setQAAction,
  role,
  approvalHistory,
  onResubmitClick,
  onQAResubmitClick,
}) {
  const getFileIconUrl = (type = "") => {
    const lowerType = type.toLowerCase();
    if (lowerType === "image/png") {
      return `${process.env.PUBLIC_URL}/assets/PNG.svg`;
    } else if (lowerType === "image/jpg" || lowerType === "image/jpeg") {
      return `${process.env.PUBLIC_URL}/assets/JPG.svg`;
    } else if (lowerType === "application/pdf") {
      return `${process.env.PUBLIC_URL}/assets/PDF.svg`;
    } else {
      return `${process.env.PUBLIC_URL}/assets/Excel.svg`;
    }
  };

  /*redcolor #cf1322 bgcolor #fff1f0 bordercolor #ffa39e*/
  /*yellowcolor #faad14 bgcolor #fffbe6 bordercolor #ffe58f*/
  /*redcolor #7cb305 bgcolor #fcffe6 bordercolor #eaff8f*/
  const bgColor = (severity) => {
    if (severity === "MINOR") {
      return "#f6ffed";
    } else if (severity === "MAJOR") {
      return "#fffbe6";
    } else if (severity === "CRITICAL") {
      return "#fff1f0";
    } else {
      return "#ffff";
    }
  };
  const borderColor = (severity) => {
    if (severity === "MINOR") {
      return "lime";
    } else if (severity === "MAJOR") {
      return "#ffe58f";
    } else if (severity === "CRITICAL") {
      return "#ffa39e";
    } else {
      return "#fff3f39f";
    }
  };
  const statusColors = {
    CLOSED: `blue`,
    PENDING_QA: `orange`,
    PENDING: `orange`,
    PENDING_CONSULTANT: `orange`,
    QA_APPROVED: `green`,
    APPROVED: `green`,
    CONSULTANT_APPROVED: `green`,
    QA_REJECTED: `red`,
    REJECTED: `red`,
    CONSULTANT_REJECTED: `red`,
    CONSULTANT_APPROVED_WITH_COMMENTS: `purple`,
    DRAFT: `default`,
    CANCELLED: `default`,
  };
  const handlereSubmit = () => {
    if (role === "contractor") {
      if (onResubmitClick) {
        onResubmitClick();
      } else {
        // Fallback to old behavior if callback not provided
        setActiveOption("form submission");
        setResubmit(true);
      }
    } else if (role === "qa" && status === "CONSULTANT_REJECTED") {
      // QA resubmit when consultant rejected
      if (onQAResubmitClick) {
        onQAResubmitClick();
      }
    } else {
      setQAAction(true);
    }
  };

  const timelineColor = (severity) => {
    switch (severity) {
      case "MINOR":
        return "green"; // or lime-like closest
      case "MAJOR":
        return "orange"; // closest to #ffe58f
      case "CRITICAL":
        return "red"; // closest to #ffa39e
      default:
        return "gray";
    }
  };

  return (
    <>
      {/* ===== Conditions ===== */}

      {(() => {
        const canViewButton = (() => {
          if (
            role === "contractor" &&
            (status === "QA_REJECTED" || status === "CONSULTANT_REJECTED")
          ) {
            return true;
          }
          if (
            role === "qa" &&
            (status === "PENDING_QA" || status === "CONSULTANT_REJECTED")
          ) {
            return true;
          }
          if (
            role === "consultant" &&
            (status === "PENDING_CONSULTANT" || status === "PENDING_REVIEW")
          ) {
            return true;
          }
          return false;
        })();
        return (
          <>
            {/* ===== Timeline Items ===== */}
            <p class="fs-16 fw-700 brand-700 text-uppercase">
              {trans("approval workflow history")}
            </p>
            {approvalHistory?.length > 0 ? (
              approvalHistory?.map((i) => (
                <>
                  <div
                    class="p-3 mb-3"
                    style={{
                      border: "1px solid rgb(240, 240, 240)",
                      borderRadius: "8px",
                      backgroundColor: "rgb(250, 250, 250)",
                    }}
                  >
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <strong>{trans(i?.step_name)}</strong>
                      <Tag color={statusColors[i?.status] || "default"}>
                        {trans(i?.status)}
                      </Tag>
                    </div>
                    <div class="text-secondary small mt-1">
                      <span class="fw-semibold">{trans("Reviewed By")}:</span>{" "}
                      {i?.reviewedByUser?.name}
                    </div>
                    <div class="text-secondary small mt-1">
                      <span class="fw-semibold">{trans("Reviewed At")}:</span>{" "}
                      {formatDateString(i?.reviewed_at)}
                    </div>
                    <div class="text-secondary small mt-2 fst-italic">
                      <span class="fw-semibold">{trans("comments")}:</span>{" "}
                      {i?.comments}
                    </div>
                  </div>
                </>
              ))
            ) : (
              <div className="text-center text-muted py-4">
                {trans("No approvals have been recorded yet")}
              </div>
            )}
            <p class="fs-16 fw-700 brand-700 text-uppercase mb-3">
              {trans("deficiencies")}
            </p>
            {values?.length > 0 ? (
              values?.map((i) => (
                <>
                  <Timeline
                    key={i?.id}
                    items={[
                      {
                        key: i?.id,
                        dot: (
                          <div
                            style={{
                              width: 12,
                              height: 12,
                              borderRadius: "50%",
                              background: borderColor(i?.severity),
                            }}
                          />
                        ),
                        color: timelineColor(i?.severity),
                        children: (
                          <div className="d-flex flex-column gap-3">
                            {/* ========== Identified Section ========== */}
                            <div>
                              <div className="fw-bold fs-6">
                                {trans("Identified Details")}
                              </div>

                              <div className="text-secondary small mt-1">
                                <span className="fw-semibold">
                                  {trans("Deficiency Severity")}:
                                </span>{" "}
                                {trans(i?.severity)}
                              </div>

                              <div className="text-secondary small mt-1">
                                <span className="fw-semibold">
                                  {trans("Deficiency Category")}:
                                </span>{" "}
                                {trans(i?.deficiency_category)}
                              </div>

                              <div className="text-secondary small mt-1">
                                <span className="fw-semibold">
                                  {trans("Description")}:
                                </span>{" "}
                                {trans(i?.description)}
                              </div>

                              <div className="text-secondary small mt-1">
                                <span className="fw-semibold">
                                  {trans("Date Identified")}:
                                </span>{" "}
                                {formatDateString(i?.identified_at)}
                              </div>

                              <div className="text-secondary small mt-1">
                                <span className="fw-semibold">
                                  {trans("Identified By")}:
                                </span>{" "}
                                {i?.identifiedByUser?.name || "-"}
                              </div>
                            </div>

                            {/* ========== Attachments Section ========== */}
                            {i?.attachments && i?.attachments?.length > 0 && (
                              <>
                                <div className="d-flex align-items-center gap-2">
                                  <h5 className="mb-0">{trans("Attachments")}</h5>
                                </div>
                                <div className="d-flex flex-column gap-3">
                                  {i?.attachments?.map((file, index) => (
                                    <div
                                      key={file?.id || index}
                                      className="d-flex justify-content-between align-items-center border p-3 rounded pointer"
                                      onClick={() => window.open(`${file?.file_url}`, "_blank")}
                                    >
                                      <div className="d-flex align-items-center gap-3">
                                        <img
                                          src={getFileIconUrl(file?.mime_type)}
                                          alt={file?.mime_type || "file"}
                                          width={28}
                                        />
                                        <div>
                                          {file?.filename ||
                                            `${trans("Attachment")} ${index + 1}`}
                                          <br />
                                          {file?.file_size && (
                                            <div className="text-muted small">
                                              {file.file_size} MB
                                            </div>
                                          )}
                                          {file?.uploadedBy && (
                                            <div className="text-muted small">
                                              {trans("Uploaded by")}{" "}
                                              <span className="fw-800">
                                                {file.uploadedBy?.name}
                                              </span>{" "}
                                              {trans("on")}{" "}
                                              <span className="fw-800">
                                                {formatDateString(file.uploaded_at)}
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      <DownloadOutlined
                                        className="text-secondary pointer"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const link = document.createElement("a");
                                          link.href = `${file?.file_url}`;
                                          link.download =
                                            file?.filename ||
                                            `attachment-${index + 1}`;
                                          document.body.appendChild(link);
                                          link.click();
                                          document.body.removeChild(link);
                                        }}
                                      />
                                    </div>
                                  ))}
                                </div>
                              </>
                            )}

                            {/* ========== Correction Section ========== */}
                            {i?.corrected && (
                              <div>
                                <div className="fw-bold fs-6">
                                  {trans("Correction Details")}
                                </div>

                                <div className="text-secondary small mt-1">
                                  <span className="fw-semibold">
                                    {trans("Notes")}:
                                  </span>{" "}
                                  {trans(i?.correction_notes)}
                                </div>

                                <div className="text-secondary small mt-1">
                                  <span className="fw-semibold">
                                    {trans("Corrected At")}:
                                  </span>{" "}
                                  {formatDateString(i?.corrected_at)}
                                </div>

                                <div className="text-secondary small mt-1">
                                  <span className="fw-semibold">
                                    {trans("Corrected By")}:
                                  </span>{" "}
                                  {i?.correctedByUser?.name || "-"}
                                </div>
                              </div>
                            )}
                          </div>
                        ),
                      },
                    ]}
                  />
                </>
              ))
            ) : (
              <div className="text-center text-muted py-4">
                {trans("No deficiencies have been recorded yet")}
              </div>
            )}

            {/* ===== Sticky Bottom Action ===== */}
            {canViewButton && (
              <div
                style={{
                  position: "sticky",
                  bottom: 0,
                  background: "#fff",
                  padding: "12px 16px",
                  borderTop: "1px solid #f0f0f0",
                  zIndex: 10,
                  display: "flex",
                  justifyContent: "flex-end",
                  transform: "translateY(10px)",
                }}
              >
                <Button
                  danger
                  text={
                    role === "contractor"
                      ? trans("Resubmit")
                      : role === "qa" && status === "CONSULTANT_REJECTED"
                      ? trans("Resubmit")
                      : trans("Review")
                  }
                  type="primary"
                  onClick={handlereSubmit}
                />
              </div>
            )}
          </>
        );
      })()}
    </>
  );
}
